import SectionTitle from './title';
import { ResolverProps } from '../utils/resolverHelpers';
import { SectionTitleType } from '../../../utils/mappers/sectionTitleMapper';
import { Box, Stack } from '@mui/material';
import { PropsWithChildren } from 'react';
import getRichTextSummary from '../../RichText/getRichTextSummary';
import LightThemeProvider from '../../../providers/LightThemeProvider';
import DarkThemeProvider from '../../../providers/DarkThemeProvider';
import RichCtaButton from '../../elements/RichCtaButton';
import Optional from '../../Optional';

type Props = Pick<ResolverProps, 'context' | 'isMobile'> & {
  data: SectionTitleType;
  variant?: string;
};

const SectionTitleContainer = ({
  data,
  children,
  context,
  isMobile,
  variant,
}: PropsWithChildren<Props>) => {
  const isCtaButton = !!data.cta && data.styles.buttonPlacement === 'bottom';
  const ThemeProvider = data.styles.isDark
    ? DarkThemeProvider
    : LightThemeProvider;

  const hasTitle =
    !!getRichTextSummary(data.title) || !!getRichTextSummary(data.subtitle);
  return (
    <ThemeProvider>
      <Stack
        component={'section'}
        data-section={context?.attrData?.type}
        data-variant={variant}
        data-index={context?.index != null ? String(context.index) : undefined}
        data-parity={
          context?.isEven != null
            ? context.isEven
              ? 'even'
              : 'odd'
            : undefined
        }
        sx={{
          py: { xs: 8, md: 10 },
          px: { xs: 2, md: 7 },
          mx: { xs: 0.5, md: 1 },
          borderRadius: { xs: 0.5, md: 1 },
          bgcolor: 'background.default',
          ...data.styles.containerSx,
        }}
        spacing={{ xs: 5, md: 10 }}
      >
        <Optional condition={hasTitle}>
          <Box
            sx={{
              ...data.styles.titleSx,
            }}
          >
            <SectionTitle data={data} isMobile={isMobile} />
          </Box>
        </Optional>
        {children}

        <Box
          sx={{
            display: {
              xs: data.cta ? 'block' : 'none',
              md: isCtaButton ? 'block' : 'none',
            },
            alignSelf: {
              xs: data.styles.align === 'center' ? 'center' : 'start',
              md: 'end',
            },
            ...data.styles.bottomButtonSx,
          }}
        >
          <RichCtaButton
            cta={data.cta}
            track={{ position: getRichTextSummary(data.title) }}
          />
        </Box>
      </Stack>
    </ThemeProvider>
  );
};

export default SectionTitleContainer;
